import { useState } from "react";
import { BuyerRequirement } from "@/mocks/buyerRequirements";

interface Props {
  req: BuyerRequirement;
  onClose: () => void;
}

export default function SendQuoteModal({ req, onClose }: Props) {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    factoryName: "TechVision Factory",
    unitPrice: "",
    moq: "",
    leadTime: "",
    sampleAvailable: "yes",
    message: `Dear ${req.buyerName},\n\nThank you for your inquiry regarding "${req.title}". We are a verified factory on StarBookPrime and would be happy to provide a competitive quotation.\n\nPlease find our offer details above. We look forward to working with you.\n\nBest regards,\nTechVision Factory`,
  });

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("factoryName", form.factoryName);
      body.append("buyerName", req.buyerName);
      body.append("email", req.email);
      body.append("requirementTitle", req.title);
      body.append("quantity", req.quantity);
      body.append("unitPrice", form.unitPrice);
      body.append("moq", form.moq);
      body.append("leadTime", form.leadTime);
      body.append("sampleAvailable", form.sampleAvailable);
      body.append("message", form.message);
      await fetch("https://readdy.ai/api/form/d7e0eakkpdh0a4ae08d0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSent(true);
    } catch {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-4xl text-green-500" />
          </div>
          <h3 className="text-lg font-extrabold text-gray-900 mb-2">Quotation Sent!</h3>
          <p className="text-sm text-gray-500 mb-1">Your quotation has been sent to:</p>
          <p className="text-sm font-bold text-orange-600 mb-1">{req.email}</p>
          <p className="text-xs text-gray-400 mb-6">
            The buyer may also contact you via WhatsApp: <strong>{req.whatsapp}</strong>
          </p>
          <button onClick={onClose} className="w-full py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-extrabold text-gray-900">Send Quotation</h3>
            <p className="text-xs text-gray-400 mt-0.5">To: {req.buyerName} · {req.company}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form data-readdy-form id="send-quote-form" onSubmit={handleSend} className="px-6 py-5 space-y-4 max-h-[72vh] overflow-y-auto">
          {/* Buyer Summary */}
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
            <p className="text-xs font-bold text-orange-700 mb-2">Responding to Requirement:</p>
            <p className="text-sm font-semibold text-gray-900 mb-1">{req.title}</p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              <span><i className="ri-box-3-line mr-1" />{req.quantity}</span>
              <span><i className="ri-global-line mr-1" />{req.countryFlag} {req.country}</span>
              <span><i className="ri-money-dollar-circle-line mr-1" />{req.budget}</span>
            </div>
          </div>

          {/* Buyer Contact (visible to factory member) */}
          <div className="bg-green-50 border border-green-100 rounded-xl p-4">
            <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1.5">
              <i className="ri-shield-check-fill" /> Buyer Contact Details
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs">
                <i className="ri-user-line text-gray-400" />
                <span className="font-semibold text-gray-800">{req.buyerName}</span>
                {req.company && <span className="text-gray-400">· {req.company}</span>}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <i className="ri-mail-line text-orange-500" />
                <a href={`mailto:${req.email}`} className="text-orange-600 hover:underline">{req.email}</a>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <i className="ri-whatsapp-line text-green-500" />
                <a href={`https://wa.me/${req.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="nofollow noreferrer" className="text-green-600 hover:underline">{req.whatsapp}</a>
              </div>
            </div>
          </div>

          {/* Quote Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Unit Price (USD)</label>
              <input name="unitPrice" type="text" value={form.unitPrice} onChange={(e) => setForm((p) => ({ ...p, unitPrice: e.target.value }))} placeholder="e.g. $12.50" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">MOQ</label>
              <input name="moq" type="text" value={form.moq} onChange={(e) => setForm((p) => ({ ...p, moq: e.target.value }))} placeholder="e.g. 1,000 units" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Lead Time</label>
              <input name="leadTime" type="text" value={form.leadTime} onChange={(e) => setForm((p) => ({ ...p, leadTime: e.target.value }))} placeholder="e.g. 30 days" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sample Available?</label>
              <select name="sampleAvailable" value={form.sampleAvailable} onChange={(e) => setForm((p) => ({ ...p, sampleAvailable: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 bg-white cursor-pointer transition-colors">
                <option value="yes">Yes — Free Sample</option>
                <option value="paid">Paid Sample</option>
                <option value="no">No Sample</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Message to Buyer <span className="text-gray-400 font-normal">({form.message.length}/500)</span>
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value.slice(0, 500) }))}
              rows={6}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors resize-none"
            />
          </div>

          <div className="flex items-start gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
            <i className="ri-information-line text-orange-400 flex-shrink-0 mt-0.5" />
            <span>This quotation will be sent to the buyer&apos;s email. They may also contact you directly via WhatsApp.</span>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin" /> Sending...</>
            ) : (
              <><i className="ri-send-plane-fill" /> Send Quotation to {req.buyerName}</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
